import React, { createContext, useContext, useEffect, useState } from 'react';
import { navigate } from '../lib/navigate';

// ── Types ─────────────────────────────────────────────────────────────────────

type ConsentState = 'pending' | 'accepted' | 'declined';

interface ConsentContextValue {
  consent: ConsentState;
  accept: () => void;
  decline: () => void;
}

const STORAGE_KEY = 'cookie_consent';

// ── Context ───────────────────────────────────────────────────────────────────

const ConsentContext = createContext<ConsentContextValue>({
  consent: 'pending',
  accept: () => {},
  decline: () => {},
});

export function useConsent() {
  return useContext(ConsentContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<ConsentState>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'accepted') return 'accepted';
    if (stored === 'declined') return 'declined';
    return 'pending';
  });

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent('declined');
  };

  return (
    <ConsentContext.Provider value={{ consent, accept, decline }}>
      {children}
    </ConsentContext.Provider>
  );
}

// ── Banner ────────────────────────────────────────────────────────────────────

export default function CookieConsent() {
  const { consent, accept, decline } = useConsent();
  const [visible, setVisible] = useState(false);

  // Small delay so the banner doesn't flash in before hydration
  useEffect(() => {
    if (consent === 'pending') {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [consent]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 pointer-events-none"
    >
      <div className="max-w-2xl mx-auto bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl shadow-black/50 p-5 pointer-events-auto animate-slide-up">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold mb-1">
              We use cookies
            </p>
            <p className="text-zinc-400 text-xs leading-relaxed">
              We and our partners use cookies and similar technologies to show
              you relevant ads and analyze site traffic. By clicking{' '}
              <strong className="text-zinc-200">Accept</strong>, you consent to
              our use of advertising cookies.{' '}
              <a
                href="/privacy"
                onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
                className="text-amber-400 underline underline-offset-2 hover:text-amber-300 transition-colors"
              >
                Privacy &amp; Cookie Policy
              </a>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0 ml-2">
            <button
              onClick={decline}
              className="px-4 py-2 text-xs font-medium text-zinc-400 hover:text-zinc-200 border border-zinc-700 hover:border-zinc-600 rounded-lg transition-all whitespace-nowrap"
            >
              Decline
            </button>
            <button
              onClick={accept}
              className="px-4 py-2 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded-lg transition-all shadow-md shadow-amber-500/20 whitespace-nowrap"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
